// Wraps a single semantic-layer measure, fetches its rows, and
// hands them to a viz component. Loading and error states live
// here so each card consistently shows status, and so a single
// dashboard rendering one failed measure doesn't take down the
// whole page.

import { useEffect, useState } from 'react';
import { api, type QueryResult, type QuerySpec } from '../../lib/api';
import { BarList } from './BarList';
import { Kpi } from './Kpi';
import { DataTable } from './DataTable';

export type Viz = 'kpi' | 'bar' | 'table';

interface Props {
  title: string;
  spec: QuerySpec;
  viz: Viz;
  description?: string;
}

export function MeasureCard({ title, spec, viz, description }: Props) {
  const [result, setResult] = useState<QueryResult | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setResult(undefined);
    setError(null);
    api
      .query(spec)
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [spec]);

  return (
    <article className="measure-card">
      <header>
        <h3>{title}</h3>
        {description && <p className="muted small">{description}</p>}
      </header>
      <div className="measure-body">
        {error && <p className="error">{error}</p>}
        {result === undefined && !error && <p className="muted">Loading…</p>}
        {result && result.rows.length === 0 && <p className="muted">No data yet.</p>}
        {result && result.rows.length > 0 && viz === 'kpi' && <Kpi rows={result.rows} />}
        {result && result.rows.length > 0 && viz === 'bar' && <BarList rows={result.rows} />}
        {result && result.rows.length > 0 && viz === 'table' && <DataTable rows={result.rows} />}
      </div>
      <style>{styles}</style>
    </article>
  );
}

const styles = `
.measure-card {
  background: var(--ac-white);
  border: 1px solid var(--ac-grey-200);
  border-radius: var(--ac-radius);
  padding: 16px 20px;
  box-shadow: var(--ac-shadow-sm);
}
.measure-card header h3 { margin: 0 0 4px; font-size: 14px; font-weight: 600; color: var(--ac-grey-700); text-transform: uppercase; letter-spacing: 0.04em; }
.measure-card header p { margin: 0 0 12px; }
.measure-body { min-height: 48px; display: flex; align-items: center; }
.measure-body p { margin: 0; }
.muted { color: var(--ac-grey-500); }
.small { font-size: 13px; }
.error { color: #7c1f1f; background: #fae3e3; padding: 6px 10px; border-radius: var(--ac-radius); font-size: 13px; }
`;
